import { useState, useEffect, useRef, useCallback } from 'react';
import {
  checkLockForPrefixo,
  checkCompletedForPrefixo,
  acquireLock as acquireLockService,
  renewHeartbeat,
  markLockAsReleased,
  markLockAsCompleted,
  LockHeldError,
  VotacaoEtapa1Lock,
  HEARTBEAT_INTERVAL_MS,
  LOCK_CHECK_INTERVAL_MS,
} from 'services/votacao-etapa1-lock.service';
import type { Etapa1Identifier } from 'services/validacao-etapa1.service';

export type LockState = 'checking' | 'available' | 'locked_by_self' | 'locked_by_other' | 'completed' | 'error';

export interface LockHolder {
  name: string;
  matricula: string;
  lockedAt: number;
}

interface UseVotacaoLockReturn {
  lockState: LockState;
  lockHolder: LockHolder | null;
  acquireLock: () => Promise<boolean>;
  releaseLock: () => Promise<void>;
  completeLock: () => Promise<void>;
  refreshLockStatus: () => Promise<void>;
}

export function useVotacaoLock(
  identifier: Etapa1Identifier,
  categoria: string,
  user?: { _id?: string; name?: string; detail?: any }
): UseVotacaoLockReturn {
  const [lockState, setLockState] = useState<LockState>('checking');
  const [lockHolder, setLockHolder] = useState<LockHolder | null>(null);

  const lockIdRef = useRef<string | null>(null);
  const heartbeatIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const userIdRef = useRef(user?._id);

  userIdRef.current = user?._id;

  const comite = identifier?.comite || '';
  const prefixo = identifier?.prefixo || '';
  const hasIdentifier = Boolean(comite || prefixo);

  const getUserMatricula = useCallback((): string => {
    return String(user?.detail?.acessor?.vp?.matricula || user?._id || '').trim();
  }, [user]);

  const getUserName = useCallback((): string => {
    return String(user?.name || '').trim();
  }, [user]);

  const clearHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
  }, []);

  const clearPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  const startHeartbeat = useCallback((lockId: string) => {
    clearHeartbeat();
    heartbeatIntervalRef.current = setInterval(() => {
      renewHeartbeat(lockId).catch(() => {});
    }, HEARTBEAT_INTERVAL_MS);
  }, [clearHeartbeat]);

  const setLockedByOther = useCallback((lock: VotacaoEtapa1Lock) => {
    setLockState('locked_by_other');
    setLockHolder({
      name: lock.lockedByName,
      matricula: lock.lockedByMatricula,
      lockedAt: lock.lockedAt,
    });
  }, []);

  const checkStatus = useCallback(async () => {
    if (!hasIdentifier || !categoria) {
      setLockState('available');
      return;
    }

    const currentIdentifier: Etapa1Identifier = comite ? { comite } : { prefixo };

    try {
      const completedLock = await checkCompletedForPrefixo(currentIdentifier, categoria);
      if (completedLock) {
        setLockState('completed');
        setLockHolder({
          name: completedLock.lockedByName,
          matricula: completedLock.lockedByMatricula,
          lockedAt: completedLock.lockedAt,
        });
        return;
      }

      const lock = await checkLockForPrefixo(currentIdentifier, categoria);

      if (!lock) {
        setLockState('available');
        setLockHolder(null);
        return;
      }

      if (lock.lockedBy === userIdRef.current) {
        setLockState('locked_by_self');
        setLockHolder(null);
        lockIdRef.current = lock._id;
        startHeartbeat(lock._id);
        return;
      }

      setLockedByOther(lock);
    } catch {
      setLockState('error');
    }
  }, [comite, prefixo, hasIdentifier, categoria, startHeartbeat, setLockedByOther]);

  const startPolling = useCallback(() => {
    clearPolling();
    pollingIntervalRef.current = setInterval(() => {
      checkStatus();
    }, LOCK_CHECK_INTERVAL_MS);
  }, [clearPolling, checkStatus]);

  const acquireLock = useCallback(async (): Promise<boolean> => {
    if (!hasIdentifier || !categoria || !user?._id) return false;

    const currentIdentifier: Etapa1Identifier = comite ? { comite } : { prefixo };

    try {
      const lock = await acquireLockService(
        currentIdentifier,
        categoria,
        user._id,
        getUserName(),
        getUserMatricula()
      );

      lockIdRef.current = lock._id;
      setLockState('locked_by_self');
      setLockHolder(null);
      startHeartbeat(lock._id);
      clearPolling();
      return true;
    } catch (err) {
      if (err instanceof LockHeldError) {
        setLockedByOther(err.lock);
        startPolling();
        return false;
      }
      setLockState('error');
      return false;
    }
  }, [comite, prefixo, hasIdentifier, categoria, user, getUserName, getUserMatricula, startHeartbeat, clearPolling, setLockedByOther, startPolling]);

  const releaseLock = useCallback(async (): Promise<void> => {
    clearHeartbeat();
    clearPolling();

    const lockId = lockIdRef.current;
    if (lockId) {
      try {
        await markLockAsReleased(lockId);
      } catch {
        // best-effort release
      }
      lockIdRef.current = null;
    }

    setLockState('available');
    setLockHolder(null);
  }, [clearHeartbeat, clearPolling]);

  const completeLock = useCallback(async (): Promise<void> => {
    clearHeartbeat();
    clearPolling();

    const lockId = lockIdRef.current;
    if (lockId) {
      await markLockAsCompleted(lockId);
      lockIdRef.current = null;
    }

    setLockState('completed');
  }, [clearHeartbeat, clearPolling]);

  const refreshLockStatus = useCallback(async (): Promise<void> => {
    setLockState('checking');
    await checkStatus();
  }, [checkStatus]);

  // Initial check on mount and when identifier/categoria changes.
  // When categoria changes while we hold a lock, release the old one first.
  useEffect(() => {
    const heldLockId = lockIdRef.current;
    if (heldLockId) {
      clearHeartbeat();
      lockIdRef.current = null;
      markLockAsReleased(heldLockId).catch(() => {});
    }
    checkStatus();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comite, prefixo, categoria]);

  // Start/stop polling based on lock state
  useEffect(() => {
    if (lockState === 'locked_by_other') {
      startPolling();
    } else {
      clearPolling();
    }

    return () => clearPolling();
  }, [lockState, startPolling, clearPolling]);

  // Cleanup on unmount + best-effort release
  useEffect(() => {
    return () => {
      clearHeartbeat();
      clearPolling();

      // Best-effort release: heartbeat timeout is the primary safety net
      const lockId = lockIdRef.current;
      if (lockId) {
        markLockAsReleased(lockId).catch(() => {});
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    lockState,
    lockHolder,
    acquireLock,
    releaseLock,
    completeLock,
    refreshLockStatus,
  };
}
